import './styles/main.css';
import umSourceUrl from './assets/tetris.umm.txt?url';
import { drawGrid } from './display/canvas';
import { Hardware } from './hardware/codes';
import { startClock } from './hardware/clock';
import { installKeyboard } from './hardware/keyboard';
import { parsePacket, type DisplayPacket } from './runtime/protocol';

const app = document.querySelector<HTMLElement>('#app');
if (!app) throw new Error('화면 장치를 찾을 수 없습니다.');

app.innerHTML = `
  <div class="shell">
    <header class="mast">
      <div>
        <div class="eyebrow">UMJUNSIK-LANG GAME COMPUTER</div>
        <h1>엄트리스</h1>
      </div>
      <div class="engine"><span class="lamp"></span><span id="engine-status">엄랭 엔진 부팅 중</span></div>
    </header>
    <section class="game" aria-label="엄트리스 게임">
      <aside class="panel left">
        <div class="stats">
          <div class="stat"><span>점수</span><strong id="score">0</strong></div>
          <div class="stat"><span>제거한 줄</span><strong id="lines">0</strong></div>
          <div class="stat"><span>레벨</span><strong id="level">1</strong></div>
          <div class="stat"><span>콤보</span><strong id="combo">0</strong></div>
        </div>
        <div class="seed-control">
          <label for="seed">시드 (0–127)</label>
          <div><input id="seed" type="number" min="0" max="127" step="1" /><button id="apply-seed">적용</button></div>
        </div>
        <div class="actions">
          <button class="action primary" id="start" aria-label="게임 시작">시작</button>
          <button class="action" id="pause" aria-label="게임 일시정지">일시정지</button>
          <button class="action" id="restart" aria-label="게임 재시작">재시작</button>
          <button class="action" id="mute" aria-label="효과음 음소거">효과음 켜짐</button>
        </div>
        <p class="help">← → 이동 · ↓ 소프트 드롭<br>↑/X 시계 회전 · Z 반시계 회전<br>Space 하드 드롭 · C 홀드 · P 일시정지</p>
      </aside>
      <div class="panel board-wrap">
        <canvas id="board" aria-label="10열 20행 엄트리스 보드" role="img"></canvas>
        <div class="overlay" id="overlay">
          <strong>엄랭 VM을 부팅하고 있습니다</strong>
          <span class="mono">어떻게…</span>
        </div>
      </div>
      <aside class="panel right">
        <p class="preview-title">HOLD</p>
        <canvas class="preview" id="hold" aria-label="홀드 블록"></canvas>
        <p class="preview-title">NEXT 01</p>
        <canvas class="preview" id="next-0" aria-label="다음 블록 첫 번째"></canvas>
        <p class="preview-title">NEXT 02</p>
        <canvas class="preview" id="next-1" aria-label="다음 블록 두 번째"></canvas>
        <p class="preview-title">NEXT 03</p>
        <canvas class="preview" id="next-2" aria-label="다음 블록 세 번째"></canvas>
      </aside>
    </section>
    <div class="touch" aria-label="모바일 조작">
      <button data-down="1" data-up="11" aria-label="왼쪽 이동">←</button>
      <button data-down="2" data-up="12" aria-label="오른쪽 이동">→</button>
      <button data-down="3" data-up="13" aria-label="아래 이동">↓</button>
      <button data-down="6" data-up="16" aria-label="하드 드롭">DROP</button>
      <button data-down="5" data-up="15" aria-label="반시계 방향 회전">↺</button>
      <button data-down="4" data-up="14" aria-label="시계 방향 회전">↻</button>
      <button data-down="7" data-up="17" aria-label="홀드">HOLD</button>
      <button data-down="8" data-up="18" aria-label="일시정지">PAUSE</button>
    </div>
    <details class="panel" id="debug-panel">
      <summary>엄랭 디버그 패널</summary>
      <pre id="debug">엄랭 소스 로딩 중…</pre>
    </details>
  </div>
`;

const boardCanvas = element<HTMLCanvasElement>('board');
const previewCanvases = [0, 1, 2].map((index) =>
  element<HTMLCanvasElement>(`next-${index}`),
);
const holdCanvas = element<HTMLCanvasElement>('hold');
const overlay = element<HTMLElement>('overlay');
const debug = element<HTMLElement>('debug');
const debugPanel = element<HTMLDetailsElement>('debug-panel');
const worker = new Worker(new URL('./runtime/worker.ts', import.meta.url), {
  type: 'module',
});

let latest: DisplayPacket | null = null;
let lastHardwareCode = -1;
let calls = 0;
let totalDuration = 0;
let maxDuration = 0;
let muted = false;
let disposed = false;
let loaded = false;
let vmReady = false;
let audioContext: AudioContext | null = null;
let generation = 0;

const send = (value: number) => {
  if (disposed || !vmReady) return;
  lastHardwareCode = value;
  worker.postMessage({ type: 'input', value });
};

worker.onmessage = (event: MessageEvent) => {
  const message = event.data;
  if (message.generation !== generation) return;
  if (message.type === 'error') {
    vmReady = false;
    element('engine-status').textContent = '엄랭 엔진 안전 정지';
    overlay.hidden = false;
    overlay.innerHTML = `<strong>엄랭 VM 오류</strong><span>${escapeHtml(message.error)}</span>`;
    debug.textContent = JSON.stringify(message, null, 2);
    return;
  }
  if (message.type !== 'packet') return;
  try {
    vmReady = true;
    latest = parsePacket(message.packet);
    calls += 1;
    totalDuration += message.duration;
    maxDuration = Math.max(maxDuration, message.duration);
    render(latest, message.snapshot, message.duration);
    playEvent(latest.eventCode);
  } catch (error) {
    vmReady = false;
    element('engine-status').textContent = '출력 패킷 오류';
    const message = error instanceof Error ? error.message : String(error);
    overlay.hidden = false;
    overlay.innerHTML = `<strong>엄랭 출력 오류</strong><span>${escapeHtml(message)}</span>`;
    debug.textContent = message;
  }
};

fetch(umSourceUrl)
  .then((response) => {
    if (!response.ok) throw new Error(`엄랭 소스 로드 실패: ${response.status}`);
    return response.text();
  })
  .then((source) => {
    loaded = true;
    const seed = initialSeed();
    element<HTMLInputElement>('seed').value = String(seed);
    generation += 1;
    worker.postMessage({ type: 'load', source, seed, generation });
  })
  .catch((error) => {
    overlay.innerHTML = `<strong>부팅 실패</strong><span>${escapeHtml(String(error))}</span>`;
  });

const stopKeyboard = installKeyboard(send);
const stopClock = startClock(send);
installTouch(send);

element('start').addEventListener('click', () => send(Hardware.START_DOWN));
element('apply-seed').addEventListener('click', () => {
  if (!loaded) return;
  const seed = readSeed();
  element<HTMLInputElement>('seed').value = String(seed);
  vmReady = false;
  generation += 1;
  worker.postMessage({ type: 'reset', seed, generation });
});
element('pause').addEventListener('click', () => send(Hardware.PAUSE_DOWN));
element('restart').addEventListener('click', () => send(Hardware.RESTART_DOWN));
element('mute').addEventListener('click', () => {
  muted = !muted;
  element('mute').textContent = muted ? '효과음 꺼짐' : '효과음 켜짐';
});

window.addEventListener('beforeunload', () => {
  disposed = true;
  stopKeyboard();
  stopClock();
  worker.postMessage({ type: 'dispose' });
  worker.terminate();
  if (audioContext) void audioContext.close();
});

function render(
  packet: DisplayPacket,
  snapshot: { pointer: number; instructionCount: number },
  duration: number,
): void {
  drawGrid(boardCanvas, packet.board, 10, 20);
  packet.previews.forEach((cells, index) =>
    drawGrid(previewCanvases[index], cells, 4, 4),
  );
  drawGrid(holdCanvas, packet.holdPreview, 4, 4);
  element('score').textContent = packet.score.toLocaleString('ko-KR');
  element('lines').textContent = String(packet.lines);
  element('level').textContent = String(packet.level);
  element('combo').textContent = String(packet.combo);
  element('engine-status').textContent = `엄랭 실행 중 · frame ${packet.frameSequence}`;

  if (!packet.gameStarted) {
    overlay.hidden = false;
    overlay.innerHTML =
      '<strong>시작할 준비가 됐습니다</strong><span>Enter 또는 시작 버튼을 누르세요</span>';
  } else if (packet.gameOver) {
    overlay.hidden = false;
    overlay.innerHTML =
      '<strong>어떻게 게임 오버가 사람이름이냐ㅋㅋ</strong><span>R 또는 재시작을 누르세요</span>';
  } else if (packet.paused) {
    overlay.hidden = false;
    overlay.innerHTML = '<strong>일시정지</strong><span>P를 눌러 계속</span>';
  } else {
    overlay.hidden = true;
  }

  if (debugPanel.open) {
    debug.textContent = [
      `protocolVersion=${packet.protocolVersion}`,
      `frameSequence=${packet.frameSequence}`,
      `eventCode=${packet.eventCode}`,
      `statusCode=${packet.statusCode}`,
      `lastHardwareCode=${lastHardwareCode}`,
      `instructionPointer=${snapshot.pointer}`,
      `instructionCount=${snapshot.instructionCount}`,
      `vmRuns=${calls}`,
      `lastDuration=${duration.toFixed(2)}ms`,
      `averageDuration=${(totalDuration / calls).toFixed(2)}ms`,
      `maxDuration=${maxDuration.toFixed(2)}ms`,
      `randomState=${packet.randomState}`,
      `bagPosition=${packet.bagPosition}`,
      '',
      `rawPacket=${packet.raw.join(' ')}`,
    ].join('\n');
  }
}

function initialSeed(): number {
  const requested = new URLSearchParams(location.search).get('seed');
  if (requested !== null && /^-?\d+$/.test(requested)) {
    return Math.min(127, Math.max(0, Number(requested)));
  }
  const bytes = new Uint8Array(1);
  crypto.getRandomValues(bytes);
  return bytes[0] & 127;
}

function readSeed(): number {
  const value = Number(element<HTMLInputElement>('seed').value);
  if (!Number.isInteger(value)) return 0;
  return Math.min(127, Math.max(0, value));
}

function installTouch(transmit: (code: number) => void): void {
  document.querySelectorAll<HTMLButtonElement>('[data-down]').forEach((button) => {
    const down = () => transmit(Number(button.dataset.down));
    const up = () => transmit(Number(button.dataset.up));
    let activePointer: number | null = null;
    const release = (event: PointerEvent) => {
      if (activePointer !== event.pointerId) return;
      activePointer = null;
      up();
    };
    button.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      if (activePointer !== null) return;
      activePointer = event.pointerId;
      button.setPointerCapture(event.pointerId);
      down();
    });
    button.addEventListener('pointerup', release);
    button.addEventListener('pointercancel', release);
    button.addEventListener('lostpointercapture', release);
  });
}

function playEvent(eventCode: number): void {
  if (muted || ![3, 5, 7, 8, 9, 10, 14].includes(eventCode)) return;
  const AudioContextClass = window.AudioContext;
  if (!AudioContextClass) return;
  audioContext ??= new AudioContextClass();
  if (audioContext.state === 'suspended') void audioContext.resume();
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.frequency.value = eventCode === 14 ? 110 : 220 + eventCode * 24;
  gain.gain.setValueAtTime(0.035, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(
    0.0001,
    audioContext.currentTime + 0.08,
  );
  oscillator.connect(gain).connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + 0.08);
}

function element<T extends HTMLElement = HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`표시 장치 누락: ${id}`);
  return found as T;
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        character
      ]!,
  );
}
